import { Add } from "@mui/icons-material"
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    Grid2 as Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material"
import log from "loglevel"
import React, { useContext, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { useFindAllProjects } from "../../api/ProjectsApi"
import { NexAlert } from "../../components/Alert/Alert"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { Label } from "../../components/Typography/Typography"
import { FlightPlanFlow } from "../../models/FlightPlanFlow"
import { ProjectInfoShort } from "../../models/projects/ProjectInfoShort"
import { ProjectStatus } from "../../models/projects/ProjectStatus"
import { routes } from "../../routing/NexRoutes"
import { RootContext } from "../../store/Context"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { Types } from "../../store/types/Types"
import { sortedByCount } from "../../tools"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useAuthStore } from "../../zstore/AuthStore"
import { useLandingPageStore } from "../../zstore/LandingPageStore"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { VersionTooltip } from "./VersionTooltip"
import { useStyles } from "./styles"

export const Landing = () => {
    // Simple Hooks
    const { classes } = useStyles()
    const { dispatch } = useContext(RootContext)

    // State Management Hooks
    const appError = useAppErrorStore((state) => state.appError)
    const clearError = useAppErrorStore((state) => state.clear)
    const user = useAuthStore((state) => state.user)
    const nexusClient = useAuthStore((state) => state.nexusClient)
    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const offices = useLocalStorageStore((state) => state.offices)
    const setOffices = useLocalStorageStore((state) => state.setOffices)
    const showInactive = useLocalStorageStore((state) => state.showInactive)
    const setShowInactive = useLocalStorageStore((state) => state.setShowInactive)
    const selectedProject = useLandingPageStore((state) => state.project)
    const setSelectedProject = useLandingPageStore((state) => state.setProject)

    // Tanstack Query Hooks
    const findAllProjectsResponse = useFindAllProjects()

    // Derived Component State
    const projectList = findAllProjectsResponse.data ?? []
    const locations: string[] = sortedByCount(
        projectList.filter((project) => project.location != null).map((project) => project.location!),
    ).reverse()
    const isProjectSelected = projectList.length && selectedProject
    const userCanInitProjects = checkToolbarAccess(ToolbarOptions.Initialize_Project)

    const links: { label: string; to: string; hidden: boolean; disabled?: boolean }[] = [
        {
            label: "Initialize New Acquisition Project",
            to: routes.projectInit,
            hidden: !userCanInitProjects,
        },
        {
            label: "Edit Existing Project Area",
            disabled: !isProjectSelected,
            to: `/${selectedProject?.project_number}${routes.projectEditor}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Edit_Project_Areas),
        },
        {
            label: "Upload New Flightplan",
            disabled: !isProjectSelected,
            to: `/${selectedProject?.project_number}${routes.flightplanUpload}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Submit_Flightplans),
        },
        {
            label: "Edit Existing Flightplan",
            disabled: !isProjectSelected,
            to: `/${selectedProject?.project_number}${routes.flightplanEditor}/${FlightPlanFlow.EDIT}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Edit_Flightplans),
        },
        {
            label: "Submit New Mission Log",
            disabled: !isProjectSelected,
            to: `/${selectedProject?.project_number}${routes.missionUpload}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Submit_Mission_Logs),
        },
        {
            label: "Edit Existing Mission Log",
            disabled: !isProjectSelected,
            to: `/${selectedProject?.project_number}${routes.missionSelect}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Edit_Mission_Logs),
        },
        {
            label: "Create Data Receipt",
            to: routes.createDataReceipt,
            hidden: !checkToolbarAccess(ToolbarOptions.Flightline_QC_Review),
        },
        {
            label: "Edit Data Receipts",
            to: routes.dataReceipts,
            hidden: !checkToolbarAccess(ToolbarOptions.Flightline_QC_Review),
        },
        {
            label: "Edit Lookup Tables",
            to: `${routes.lookupEditor}`,
            hidden: !checkToolbarAccess(ToolbarOptions.Edit_Lookup_Tables),
        },
        {
            label: "Manage Sensor Installations",
            to: `${routes.sensorInstallations}`,
            hidden: !checkToolbarAccess([ToolbarOptions.Edit_Mission_Logs, ToolbarOptions.Mission_Admin]),
        },
    ]

    const showProjects = useMemo(() => {
        const projectsToShow: ProjectInfoShort[] =
            offices.length === 0 ? projectList : projectList.filter((project) => offices.includes(project.location!))

        return showInactive ? projectsToShow : projectsToShow.filter((pl) => pl.project_status === ProjectStatus.ACTIVE)
    }, [projectList, showInactive, offices])

    // Use Effects
    useEffect(() => {
        clearError()
        dispatch({ type: Types.Missions_ResetState })
    }, [])

    // Handler Functions
    const handleInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowInactive(e.target.checked)
    }

    const handleChange = (value: string | null) => {
        const selectedProject = projectList.find((project) => project.project_number === value)
        setSelectedProject(selectedProject)
    }

    return (
        <Container maxWidth="lg" className={classes.root}>
            <Grid container>
                <Grid size={{ xs: 12 }} className={classes.flex_center}>
                    <Typography variant="h4" className={classes.title}>
                        Nexus Uploader
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        {user?.name} {nexusClient && `\u2022 ${nexusClient}`}
                    </Typography>
                </Grid>

                <Grid container size={{ xs: 12 }} sx={{ alignItems: "center", mb: 2 }} spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <NexAutocomplete
                            multiple
                            fullWidth
                            sx={{ minWidth: 250 }}
                            TextFieldProps={{ variant: "standard", label: "Filter Projects by Office" }}
                            options={locations}
                            value={offices}
                            onChange={(_, locations) => setOffices(locations)}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showInactive}
                                    onChange={handleInactiveChange}
                                    name="filterInactive"
                                    size={"small"}
                                />
                            }
                            sx={{ mr: 0 }}
                            label={<Typography variant="body2">Include Inactive/Dormant</Typography>}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant={"outlined"}
                            onClick={() => {
                                setOffices([])
                                setShowInactive(false)
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Label gutterBottom="sm">Select Project</Label>
                    <NexAutocomplete
                        sx={{ mb: 2 }}
                        TextFieldProps={{ placeholder: "Select a project or start typing..." }}
                        noOptionsText={"No matches. Don't see your project? It may need to be initialized."}
                        value={selectedProject ?? null}
                        options={showProjects}
                        isOptionEqualToValue={(opt, val) => opt.project_number === val.project_number}
                        renderOption={(props, opt: ProjectInfoShort) => {
                            return (
                                <Box component={"li"} {...props} key={opt.project_number}>
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{ display: "flex", jc: "space-between", ai: "center", width: 1 }}
                                        key={opt.project_number}
                                    >
                                        <Grid size={"grow"}>
                                            <Typography>
                                                {opt.project_number} - {opt.project_long_name}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ sm: 3, md: 2 }} sx={{ textAlign: "right" }}>
                                            <Typography
                                                sx={{ fontWeight: opt.project_status !== "A" ? "bold" : "auto" }}
                                            >
                                                {getStatusString(opt.project_status!)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )
                        }}
                        getOptionLabel={(opt: ProjectInfoShort) => `${opt.project_number} - ${opt.project_long_name}`}
                        onChange={(_, value) => handleChange(value?.project_number ?? null)}
                        fullWidth
                        loading={findAllProjectsResponse.isPending}
                        endAdornment={
                            !selectedProject &&
                            userCanInitProjects && (
                                <Tooltip title={"Initialize New Acquisition Project"}>
                                    <IconButton size={"small"} component={Link} to={routes.projectInit}>
                                        <Add />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                        testId={"project-select"}
                    />

                    {appError && (
                        <NexAlert
                            animate
                            onClose={() => {
                                clearError()
                            }}
                            gutterBottom={"sm"}
                        >
                            {appError}
                        </NexAlert>
                    )}
                    {findAllProjectsResponse.error?.message && (
                        <NexAlert animate gutterBottom={"sm"}>
                            {findAllProjectsResponse.error?.message}
                        </NexAlert>
                    )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                        {links
                            .filter((link) => !link.hidden)
                            .map((link) => (
                                <Grid size={{ xs: 12, sm: 6 }} className={classes.button} key={link.label}>
                                    <Button
                                        component={Link}
                                        key={link.to}
                                        to={link.to}
                                        disabled={link.disabled}
                                        className={classes.button}
                                        fullWidth
                                        color="primary"
                                        variant="contained"
                                        size="large"
                                    >
                                        {link.label}
                                    </Button>
                                </Grid>
                            ))}
                    </Grid>
                </Grid>
            </Grid>
            <Grid className={classes.version}>
                <VersionTooltip />
            </Grid>
        </Container>
    )
}

const getStatusString = (s: ProjectStatus) => {
    switch (s) {
        case ProjectStatus.ACTIVE:
            return "Active"
        case ProjectStatus.INACTIVE:
            return "Inactive"
        case ProjectStatus.DORMANT:
            return "Dormant"
        default:
            log.error(`project with status ${s} not handled`)
            return undefined
    }
}
